import React, { useState, useEffect } from 'react';
import { canEdit } from '../utils/permissions';
import { vehicleService } from '../services/vehicleService';
import { printReport } from '../utils/printUtils'; // 🚀 신규 연동 공용 인쇄 유틸 로드

const Vehicle = () => {
  const [vehicles, setVehicles] = useState([]); 
  const [masterModels, setMasterModels] = useState([]); 
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  
  // 차종별 구분을 위한 필터 상태
  const [filterModel, setFilterModel] = useState('전체');

  const initialForm = { id: null, carNumber: '', vin: '', modelName: '', customsDate: '', deliveryDate: '', undercover: '미장착', color: '화이트', specialEquip: '', warrantyYears: '3', warrantyExpiry: '' };
  const [formData, setFormData] = useState(initialForm);

  const refresh = async () => {
    setLoading(true);
    const m = await vehicleService.getVehicleModels();
    setMasterModels(m || []);
    const v = await vehicleService.getVehicles();
    setVehicles(v || []);
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  useEffect(() => {
    if (formData.deliveryDate && formData.deliveryDate.length >= 10) {
      const exp = vehicleService.calculateWarrantyExpiry(formData.deliveryDate, formData.warrantyYears);
      if (exp && !exp.includes('NaN')) setFormData(prev => ({ ...prev, warrantyExpiry: exp }));
    }
  }, [formData.deliveryDate, formData.warrantyYears]);

  const onSave = async (e) => {
    e.preventDefault();
    if (!formData.carNumber || !formData.vin) return alert("차량번호와 차대번호는 필수입니다.");
    
    // 🚀 [비즈니스 중복 검증 방어막] 등록/수정 전에 차량번호 및 차대번호 중복 여부를 실시간 체크
    const dupMessage = vehicleService.checkDuplicate(vehicles, formData);
    if (dupMessage) {
      return alert(dupMessage);
    }

    setLoading(true);
    try {
      // 🚀 vehicleService.saveVehicle(id, data) 아키텍처 규격에 완벽 매칭
      if (isEditing) {
        await vehicleService.saveVehicle(formData.id, formData);
        alert("✅ 차량 정보가 성공적으로 수정되었습니다.");
      } else {
        await vehicleService.saveVehicle(null, formData);
        alert("✅ 신규 차량 등록이 성공적으로 완료되었습니다.");
      }
      
      setFormData(initialForm); 
      setIsEditing(false); 
      await refresh();
    } catch (err) {
      console.error("차량 저장 중 오류 발생:", err);
      alert("차량 저장 중 오류가 발생했습니다. 백엔드 서비스 구성을 확인하세요.");
    }
    setLoading(false);
  };

  const handleEdit = (v) => {
    setFormData({ ...initialForm, ...v });
    setIsEditing(true);
    window.scrollTo(0, 0);
  };

  const displayDate = (d) => (!d || String(d).includes('NaN')) ? '-' : d;

  // 🚀 [공용 리포트 시스템 전면 교체] 독립 팝업 출력 구현
  const handlePrintVehicleList = () => {
    const title = "등록 차량 마스터 현황 내역서";
    
    // 테이블 열 이름 정의
    const headers = ["차량번호", "모델명", "차대번호", "출고(인도)일", "보증만료일"];
    
    // 데이터 가공 행렬 빌드
    const rows = filteredVehicles.map(v => [
      v.carNumber || '',
      v.modelName || '',
      v.vin || '',
      displayDate(v.deliveryDate),
      displayDate(v.warrantyExpiry)
    ]);

    // 상단 메타 검색 필터 기재용 객체 생성
    const metaInfo = {
      "조회 차종": filterModel,
      "검색 조건": searchTerm ? `"${searchTerm}"` : "전체 내역"
    };

    // 공용 마스터 출력 유틸 호출
    printReport(title, headers, rows, metaInfo);
  };

  // 차량 목록 전용 EXCEL 다운로드 기능
  const handleDownloadVehicleExcel = () => {
    if (filteredVehicles.length === 0) {
      alert("다운로드할 차량 데이터가 없습니다.");
      return;
    }

    const headers = ["차량번호", "모델명", "차대번호", "통관일", "출고(인도)일", "보증기간(년)", "보증만료일", "언더커버", "색상"];
    const rows = filteredVehicles.map(v => [
      `"${v.carNumber || ''}"`,
      `"${v.modelName || ''}"`,
      `"${v.vin || ''}"`,
      `"${v.customsDate || '-'}"`,
      `"${v.deliveryDate || '-'}"`,
      `"${v.warrantyYears || '3'}년"`,
      `"${displayDate(v.warrantyExpiry)}"`,
      `"${v.undercover || '미장착'}"`,
      `"${v.color || '화이트'}"`
    ]);

    const csvContent = "\uFEFF" + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `차량_마스터_목록_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const safeVehicles = vehicles || [];
  const filteredVehicles = safeVehicles.filter(v => {
    // 🚀 [필터 고도화] 검색어 하나로 차량번호 혹은 차대번호(VIN) 모두 교차 검색 가능하도록 개편
    const searchLow = searchTerm.toLowerCase();
    const matchSearch = (v.carNumber || '').toLowerCase().includes(searchLow) || 
                        (v.vin || '').toLowerCase().includes(searchLow);
                        
    const dbModelClean = String(v.modelName || '').replace(/\s+/g, '').toLowerCase();
    const selectedModelClean = filterModel.replace(/\s+/g, '').toLowerCase();
    const matchModel = filterModel === '전체' || dbModelClean === selectedModelClean || dbModelClean.includes(selectedModelClean);

    return matchSearch && matchModel;
  });

  const style = {
    wrapper: { padding: '40px 100px', background: '#f8fafc', minHeight: '100vh' },
    card: { background: 'white', padding: '35px', borderRadius: '20px', boxShadow: '0 4px 25px rgba(0,0,0,0.05)', marginBottom: '30px' },
    input: { width: '100%', padding: '12px', border: '1px solid #e2e8f0', borderRadius: '10px', boxSizing: 'border-box', outline: 'none' },
    label: { fontSize: '12px', fontWeight: 'bold', color: '#475569', marginBottom: '5px', display: 'block' },
    grid3: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px', marginBottom: '20px' },
    btnPrimary: { padding: '15px 40px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer' },
    btnGhost: { padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', color: '#64748b' },
    
    actionPrintBtn: { padding: '10px 18px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    actionExcelBtn: { padding: '10px 18px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    filterSelect: { padding: '10px 15px', border: '2px solid #10b981', borderRadius: '8px', background: 'white', outline: 'none', cursor: 'pointer', fontWeight: '500', width: '150px' },
    
    // 🚀 [추가 스타일] 테이블 영역 스크롤 및 헤더 고정용 스타일 정의
    tableContainer: { width: '100%', maxHeight: '500px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '10px' },
    thSticky: { padding: '15px', position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1, boxShadow: '0 1px 0 #eee' }
  };

  return (
    <div style={style.wrapper}>
      {/* 상단 차량 데이터 등록 폼 카드 */}
      <div style={style.card}>
        <h2 style={{ margin: '0 0 25px 0' }}>🚗 차량 마스터 관리</h2>
        <form onSubmit={onSave}>
          <div style={style.grid3}>
            <div><label style={style.label}>1. 차량번호 *</label><input style={style.input} value={formData.carNumber} onChange={e=>setFormData({...formData, carNumber:e.target.value})} /></div>
            <div><label style={style.label}>2. 차대번호(VIN) *</label><input style={style.input} value={formData.vin} onChange={e=>setFormData({...formData, vin:e.target.value})} /></div>
            <div><label style={style.label}>3. 모델선택</label>
              <select style={style.input} value={formData.modelName} onChange={e=>setFormData({...formData, modelName:e.target.value})}>
                <option value="">-- 모델 선택 --</option>
                {masterModels.map(m => <option key={m.id} value={m.modelName}>{m.modelName}</option>)}
              </select>
            </div>
          </div>
          <div style={style.grid3}>
            <div><label style={style.label}>4. 통관일</label><input type="date" style={style.input} value={formData.customsDate} onChange={e=>setFormData({...formData, customsDate:e.target.value})} /></div>
            <div><label style={style.label}>5. 출고(인도)일</label><input type="date" style={style.input} value={formData.deliveryDate} onChange={e=>setFormData({...formData, deliveryDate:e.target.value})} /></div>
            <div><label style={style.label}>6. 보증기간(년)</label>
              <select style={style.input} value={formData.warrantyYears} onChange={e=>setFormData({...formData, warrantyYears:e.target.value})}>
                <option value="1">1년</option><option value="2">2년</option><option value="3">3년</option><option value="5">5년</option>
              </select>
            </div>
          </div>
          <div style={style.grid3}>
            <div><label style={style.label}>7. 보증만료일 (자동계산)</label><input style={{...style.input, background:'#f8fafc'}} value={displayDate(formData.warrantyExpiry)} readOnly /></div>
            <div><label style={style.label}>8. 언더커버</label>
              <select style={style.input} value={formData.undercover} onChange={e=>setFormData({...formData, undercover:e.target.value})}>
                <option value="장착">장착</option><option value="미장착">미장착</option>
              </select>
            </div>
            <div><label style={style.label}>9. 색상</label><input style={style.input} value={formData.color} onChange={e=>setFormData({...formData, color:e.target.value})} /></div>
          </div>
          <div style={{display:'flex', gap:'10px', justifyContent:'flex-end'}}>
            {isEditing && <button type="button" onClick={()=>{setFormData(initialForm); setIsEditing(false);}} style={style.btnGhost}>취소</button>}
            {canEdit() && <button type="submit" disabled={loading} style={style.btnPrimary}>{isEditing ? "수정 완료" : "차량 등록"}</button>}
          </div>
        </form>
      </div>

      {/* 하단 차량 마스터 테이블 카드 */}
      <div style={style.card}>
        <div style={{display:'flex', justifyContent:'space-between', marginBottom:'20px', alignItems:'center'}}>
          <h3>📦 등록 차량 목록 (총 {filteredVehicles.length}대)</h3>
          
          <div style={{display:'flex', gap:'10px', alignItems:'center'}}>
            <button style={style.actionPrintBtn} onClick={handlePrintVehicleList}>
              🖨️ 출력하기
            </button>
            <button style={style.actionExcelBtn} onClick={handleDownloadVehicleExcel}>
              📊 EXCEL 다운로드
            </button>

            <select
              style={style.filterSelect}
              value={filterModel}
              onChange={e => setFilterModel(e.target.value)}
            >
              <option value="전체">전체 차종</option>
              {masterModels.map(m => (
                <option key={m.id} value={m.modelName}>{m.modelName}</option>
              ))}
            </select>

            {/* 🚀 [디자인 매핑] 차량번호와 차대번호 모두 검색 가능함을 플레이스홀더에 명시 */}
            <input style={{padding:'10px', width:'250px', borderRadius:'8px', border:'1px solid #ddd', outline:'none'}} placeholder="차량번호 또는 차대번호 검색..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} />
          </div>
        </div>
        
        {/* 🚀 [변경사항] 테이블을 스크롤이 가능한 div(tableContainer)로 감싸고 최대 높이를 500px로 제한 */}
        <div style={style.tableContainer}>
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              {/* 🚀 [변경사항] 스크롤 시 최상단 헤더 열이 고정되도록 thSticky 스타일 적용 */}
              <tr style={{textAlign:'left', color:'#666'}}>
                <th style={style.thSticky}>No</th>
                <th style={style.thSticky}>차량번호(모델)</th>
                <th style={{...style.thSticky, paddingLeft: '15px'}}>차대번호</th>
                <th style={{...style.thSticky, paddingLeft: '15px'}}>출고일</th>
                <th style={{...style.thSticky, color:'#e74c3c', paddingLeft: '15px'}}>만료일</th>
                <th style={{...style.thSticky, textAlign: 'center'}}>관리</th>
              </tr>
            </thead>
            <tbody>
              {filteredVehicles.map((v, idx) => (
                <tr key={v.id} style={{borderBottom:'1px solid #f9f9f9'}}>
                  <td style={{ padding: '15px', fontWeight: '500', color: '#64748b' }}>{idx + 1}</td>
                  <td style={{padding:'15px'}}><b>{v.carNumber}</b><br/><span style={{fontSize:'12px', color:'#666'}}>{v.modelName}</span></td>
                  <td style={{padding:'15px'}}>{v.vin}</td>
                  <td style={{padding:'15px'}}>{displayDate(v.deliveryDate)}</td>
                  <td style={{padding:'15px', color:'#e74c3c', fontWeight:'bold'}}>{displayDate(v.warrantyExpiry)}</td>
                  <td style={{padding:'15px', textAlign: 'center'}}>
                    <button onClick={() => handleEdit(v)} style={{marginRight:'5px', border:'none', background:'#f1f5f9', padding:'5px 10px', borderRadius:'5px', cursor:'pointer'}}>수정</button>
                    {canEdit() && <button onClick={async () => {if(window.confirm("삭제할까요?")){await vehicleService.deleteVehicle(v.id); refresh();}}} style={{border:'none', background:'none', color:'#e74c3c', cursor:'pointer'}}>삭제</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Vehicle;